import merge from 'lodash.merge';
import { datadogLogs, HandlerType } from '@datadog/browser-logs';
import 'cross-fetch/polyfill';
import { nanoid } from 'nanoid';
import { v4 } from 'uuid';
import * as tweetnacl from 'tweetnacl';
import * as tweetnaclUtil from 'tweetnacl-util';
import difference from 'lodash.difference';
import mapValues from 'lodash.mapvalues';
import groupBy from 'lodash.groupby';

var version = "6.11.0";

const VERSION = version;
var RealtimeRole$1;
(function (RealtimeRole) {
  RealtimeRole["Provider"] = "Provider";
  RealtimeRole["Patient"] = "Patient";
  RealtimeRole["System"] = "System";
  RealtimeRole["Spectator"] = "Spectator";
  RealtimeRole["Auditor"] = "Auditor";
})(RealtimeRole$1 || (RealtimeRole$1 = {}));
var RealtimeEventType;
(function (RealtimeEventType) {
  RealtimeEventType["PreJoin"] = "pre-join";
  RealtimeEventType["Join"] = "join";
  RealtimeEventType["Leave"] = "leave";
  RealtimeEventType["Message"] = "message";
  RealtimeEventType["SetState"] = "set-state";
  RealtimeEventType["Status"] = "status";
  RealtimeEventType["MessageAck"] = "messageAck";
})(RealtimeEventType || (RealtimeEventType = {}));
var RealtimeStatus;
(function (RealtimeStatus) {
  RealtimeStatus["Connected"] = "Connected";
  RealtimeStatus["NetworkUp"] = "NetworkUp";
  RealtimeStatus["NetworkDown"] = "NetworkDown";
  RealtimeStatus["NetworkIssues"] = "NetworkIssues";
  RealtimeStatus["ConnectionError"] = "ConnectionError";
  RealtimeStatus["Timeout"] = "Timeout";
  RealtimeStatus["AccessDenied"] = "AccessDenied";
  RealtimeStatus["Other"] = "Other";
})(RealtimeStatus || (RealtimeStatus = {}));
var Endpoint;
(function (Endpoint) {
  Endpoint["Join"] = "join";
  Endpoint["Leave"] = "leave";
})(Endpoint || (Endpoint = {}));
// default value of retries for async operation
const DEFAULT_RETRY_OPTIONS = Object.freeze({
  retries: 5,
  delay: 500,
  delayMultiplier: 2,
  isExponential: true // if isExponential true - delay and delayMultiplier will be ignored
});
const ABLY_REST_HOST = 'primary.ably.realtime.doxy.me';
const ABLY_REALTIME_HOST = 'primary.ably.realtime.doxy.me';
const ABLY_FALLBACK_HOSTS = ['fallback-a.ably.realtime.doxy.me', 'fallback-b.ably.realtime.doxy.me', 'fallback-c.ably.realtime.doxy.me', 'fallback-d.ably.realtime.doxy.me', 'fallback-e.ably.realtime.doxy.me'];

/**
 * Common error class
 */
class RealtimeError extends Error {
  constructor(message) {
    super(message);
    this.name = this.constructor.name;
    this.message = message;
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(this, this.constructor);
    } else {
      this.stack = new Error(message).stack;
    }
    // https://github.com/Microsoft/TypeScript/wiki/Breaking-Changes#extending-built-ins-like-error-array-and-map-may-no-longer-work
    Object.setPrototypeOf(this, RealtimeError.prototype);
  }
}
/**
 * Error class for backend errors
 */
class BackendError extends RealtimeError {
  /**
   * Additional metadata for the error to be used for logging
   */
  meta;
  statusCode;
  constructor(message, statusCode, meta) {
    super(message);
    this.statusCode = statusCode;
    this.meta = meta;
    Object.setPrototypeOf(this, BackendError.prototype);
  }
}
/**
 * Error class for backend errors related to authentication/authorization
 */
class AuthError extends BackendError {
  constructor(message, statusCode, meta) {
    super(message, statusCode, meta);
    Object.setPrototypeOf(this, AuthError.prototype);
  }
}
/**
 * Error class to simulate error, which can include status
 */
class ClientError extends RealtimeError {
  status;
  constructor(message, status) {
    super(message);
    this.status = status;
    Object.setPrototypeOf(this, BackendError.prototype);
  }
}
const getErrorStack = error => error?.stack || error;
const logError$2 = (logger, error, action, meta = {}) => {
  logger.error({
    action,
    message: error?.message ?? JSON.stringify(error),
    meta: {
      error,
      errorStack: getErrorStack(error),
      ...meta
    }
  });
  return error;
};
const getErrorMessage = (error, fallbackMessage) => {
  return error?.message ?? fallbackMessage;
};

/* istanbul ignore file */
/**
 * Log level, error used for error, warn for warnings, info for some important information which
 * is not error or warn use debug for everything else you need, can be anything, as it is mostly
 * for development
 */
var LogLevel;
(function (LogLevel) {
  LogLevel["error"] = "error";
  LogLevel["warn"] = "warn";
  LogLevel["info"] = "info";
  LogLevel["debug"] = "debug";
})(LogLevel || (LogLevel = {}));
/**
 * Map level to console method, especially valid for local logs, not sure if it is for remote
 */
const CONSOLE_METHOD_BY_LEVEL = {
  [LogLevel.error]: 'error',
  [LogLevel.warn]: 'warn',
  [LogLevel.info]: 'info',
  [LogLevel.debug]: 'log'
};
/**
 * Level to priority map
 * Is used to easier check if level from the log item has the same or higher priority as currently selected global level
 */
const PRIORITY_BY_LEVEL = {
  [LogLevel.error]: 0,
  [LogLevel.warn]: 1,
  [LogLevel.info]: 2,
  [LogLevel.debug]: 3
};
class BaseLogger {
  title;
  action;
  message;
  meta;
  /**
   * Select log level, every level includes itself and all the above levels (info includes info, warn and error)
   */
  static level = LogLevel.info;
  static context = {};
  /**
   * Create new logger instance with predefined log properties.
   */
  constructor({
    title,
    action,
    message,
    meta
  } = {}) {
    this.title = title;
    this.action = action;
    this.message = message;
    this.meta = meta;
  }
  /**
   * Set global log level
   */
  static setLevel(level) {
    this.level = level;
  }
  /**
   * Get global log level
   */
  static getLevel() {
    return this.level;
  }
  /**
   * Set global context
   */
  static setContext(context = {}) {
    this.context = context;
  }
  /**
   * Get global context
   */
  static getContext() {
    return this.context;
  }
  error(logItem) {
    this.log({
      ...logItem,
      level: LogLevel.error
    });
  }
  warn(logItem) {
    this.log({
      ...logItem,
      level: LogLevel.warn
    });
  }
  info(logItem) {
    this.log({
      ...logItem,
      level: LogLevel.info
    });
  }
  debug(logItem) {
    this.log({
      ...logItem,
      level: LogLevel.debug
    });
  }
  /**
   * Check if current level matches the priority with log item and internal _log method
   */
  log(logItem) {
    if (PRIORITY_BY_LEVEL[logItem.level] > PRIORITY_BY_LEVEL[BaseLogger.level]) {
      return;
    }
    this._log(this._getLogItem(logItem));
  }
  /**
   * Return logger instance, accepts parameters - logs properties, that will be persisted
   */
  bind(bindObject) {
    return new BaseLogger(this.getMergedBindObject(bindObject));
  }
  getMergedBindObject(bindObject) {
    const keys = ['title', 'action', 'message', 'meta'];
    return merge(Object.fromEntries(keys.filter(key => this[key] !== undefined).map(key => [key, this[key]])), bindObject);
  }
  /**
   * Main log method, actual logging, depending on level and format
   */
  _log(logItem) {
    const method = CONSOLE_METHOD_BY_LEVEL[logItem.level || LogLevel.debug];
    if (!console[method]) {
      return;
    }
    console[method](JSON.stringify(merge({}, BaseLogger.context, logItem)));
  }
  /**
   * Get log item based on provided and bound values
   */
  _getLogItem(logItem) {
    const keys = ['title', 'action', 'message', 'meta'];
    return merge({
      date: new Date()
    }, Object.fromEntries(keys.filter(key => this[key] !== undefined).map(key => [key, this[key]])), logItem);
  }
}
const baseLogger = new BaseLogger();

const SERVICE_NAME = 'realtime-client';
const ENV_DEV = 'development';
const REDACTED_PLACEHOLDER = '<redacted>';
/**
 * Override common logger to use datadog
 */
class ClientLogger extends BaseLogger {
  static datadogLogger;
  /**
   * Set global context
   */
  static setContext(context = {}) {
    if (this.datadogLogger) {
      this.datadogLogger.setContext(context);
    }
    super.setContext(context);
  }
  /**
   * To make logging work, you need to init or override logger;
   */
  /**
   * Logger can be initialized with needed parameters,
   * clientToken must be provided
   */
  static init({
    env = ENV_DEV,
    trackSessionAcrossSubdomains = true,
    beforeSend = this.beforeSend,
    ...rest
  }, handler, level = this.level, context = {}) {
    datadogLogs.init({
      ...rest,
      env,
      trackSessionAcrossSubdomains,
      beforeSend,
      // I think it should not be possible to override these props
      service: SERVICE_NAME,
      version: VERSION
    });
    // create new instance
    this.datadogLogger = datadogLogs.createLogger(SERVICE_NAME, {
      level,
      handler: this.getDefaultHandlers(env),
      context
    });
    if (level) {
      BaseLogger.setLevel(level);
    }
    if (Object.keys(context).length > 0) {
      this.setContext(context);
    }
  }
  static getDefaultHandlers(env) {
    return env === ENV_DEV ? HandlerType.console : HandlerType.http;
  }
  /**
   * Default beforeSend implementation, replace all url params values with empty string
   * we can only assign properties to log, not return new object
   */
  static beforeSend(log) {
    log.view.url = ClientLogger.resetUrlParams(log.view.url);
    log.view.referrer = log.view.referrer && ClientLogger.resetUrlParams(log.view.referrer);
  }
  static resetUrlParams(urlString) {
    const url = new URL(urlString);
    const params = url.searchParams;
    for (const [key] of params.entries()) {
      params.set(key, '');
    }
    url.search = params.toString();
    return url.toString();
  }
  /**
   * Logger can be overridden with external datadog logger instance
   */
  static override(logger) {
    ClientLogger.datadogLogger = logger;
  }
  /**
   * Override parent log method with datadog logger logs, if not initialized, use base log method.
   */
  _log(logItem) {
    if (ClientLogger.datadogLogger) {
      ClientLogger.datadogLogger.log(logItem.message || '', logItem, logItem.level);
    } else {
      super._log(logItem);
    }
  }
  /**
   * Return logger instance, accepts parameters - logs properties, that will be persisted
   */
  bind(bindObject) {
    return new ClientLogger(this.getMergedBindObject(bindObject));
  }
}
const clientLogger = new ClientLogger();
function redactString(str, visibleChars = 8) {
  if (visibleChars <= 0 || str.length <= visibleChars) {
    return REDACTED_PLACEHOLDER;
  }
  return `${str.slice(0, visibleChars)}-${REDACTED_PLACEHOLDER}`;
}
function redactAccessTokens(value) {
  return value.replace(/access_token([=:]\s*)([^&\s;]+)/gi, (_, separator, token) => `access_token${separator}${redactString(token)}`);
}
function redactLogMessage(message) {
  return redactAccessTokens(message);
}

var RequestMethod;
(function (RequestMethod) {
  RequestMethod["Get"] = "GET";
  RequestMethod["Post"] = "POST";
  RequestMethod["Put"] = "PUT";
  RequestMethod["Delete"] = "DELETE";
})(RequestMethod || (RequestMethod = {}));
// common headers for all request methods
const COMMON_HEADERS = {
  Accept: 'application/json'
};
// common headers for requests with data (post and put)
const CONTENT_HEADERS = {
  'Content-Type': 'application/json'
};
/**
 * Core method to make http requests
 */
const jsonfetch = async (url, method, data, headers = {}, options = {}) => {
  let requestBody;
  // Binary data should not be stringified
  if (data !== undefined) {
    requestBody = data instanceof ArrayBuffer || data instanceof Blob || data instanceof ReadableStream || data instanceof Buffer || data instanceof Uint8Array ? data : JSON.stringify(data);
  }
  const response = await fetch(url, {
    method,
    headers: {
      ...COMMON_HEADERS,
      ...headers
    },
    body: requestBody,
    ...options
  });
  if (response.status === 204) {
    return null;
  }
  let body = null;
  try {
    body = await response.json();
  } catch {
    return null;
  }
  if (response.ok) {
    return body;
  }
  const message = [body?.error?.name, body?.error?.message].filter(Boolean).join(': ') || [body?.error, body?.message].filter(Boolean).join(': ');
  const error = response.status >= 400 && response.status < 500 ? new AuthError(message, response.status) : new BackendError(message, response.status);
  throw error;
};
/**
 * Get url with get params, helps to easy use this method by just passing params as object with keys
 */
const getUrlWithParams = (url, params) => {
  if (!params) {
    return url;
  }
  const newUrl = new URL(url);
  newUrl.search = new URLSearchParams(params).toString();
  return newUrl.toString();
};
const get = async (url, headers, params, options) => jsonfetch(getUrlWithParams(url, params), RequestMethod.Get, undefined, headers, options);
const post = async (url, data, headers, options) => jsonfetch(url, RequestMethod.Post, data, {
  ...CONTENT_HEADERS,
  ...headers
}, options);
const put = async (url, data, headers, options) => jsonfetch(url, RequestMethod.Put, data, {
  ...CONTENT_HEADERS,
  ...headers
}, options);

var index$3 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  get: get,
  post: post,
  put: put
});

const SHORT_ID_LENGTH = 14;
/**
 * We generate 14 symbols id, for our use case can be much smaller,
 * I selected 14 because it is max id size 'shortid' (deprecated) lib generates (7-14)
 */
const generateShortId = () => nanoid(SHORT_ID_LENGTH);
/**
 * Generate uuid version 4, maybe we can use crypto.randomUUID?
 */
const generateUuid = () => v4();

var index$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  SHORT_ID_LENGTH: SHORT_ID_LENGTH,
  generateShortId: generateShortId,
  generateUuid: generateUuid
});

const ID_SEPARATOR = '~';
/**
 * Generate realtimeId based on role, userId from id (if exists) and random part
 * for clients with no id (patients/server) we duplicate variablePart to have same structure of realtimeId
 */
const generate = (role, userId) => {
  const randomPart = userId ? generateShortId() : generateUuid();
  return `${role}${ID_SEPARATOR}${randomPart}${userId ? ID_SEPARATOR : ''}${userId || ''}`;
};
/**
 * Get role from realtimeId
 */
const getRole = realtimeId => {
  const role = realtimeId.split(ID_SEPARATOR)[0];
  if (!Object.values(RealtimeRole$1).includes(role)) {
    throw new RealtimeError(`Incomprehensible role: ${role}`);
  }
  return role;
};
/**
 * Get userId from realtimeId, for users it will be id from db model, otherwise - variable part
 */
const getUserId = realtimeId => realtimeId.split(ID_SEPARATOR)[2];
/**
 * Get realtime identity object from realtimeId
 */
const getIdentity = realtimeId => ({
  role: getRole(realtimeId),
  id: realtimeId,
  userId: getUserId(realtimeId)
});

var index$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  generate: generate,
  getIdentity: getIdentity,
  getRole: getRole,
  getUserId: getUserId
});

const logger$3 = new ClientLogger({
  title: 'Encryption'
});
const ONE_TIME_CODE_LENGTH = 24;
/**
 * Sign message with private signature key
 */
const sign = (payload, keys) => {
  if (!keys.sign) {
    return {
      payload
    };
  }
  const signedPayload = tweetnacl.sign(tweetnaclUtil.decodeUTF8(JSON.stringify(payload)), tweetnaclUtil.decodeBase64(keys.sign.private));
  return {
    payload: tweetnaclUtil.encodeBase64(signedPayload),
    signatureData: true
  };
};
/**
 * Verify message with public signature key
 */
const verify = (message, senderKeys) => senderKeys.sign ? JSON.parse(tweetnaclUtil.encodeUTF8(tweetnacl.sign.open(tweetnaclUtil.decodeBase64(message.payload), tweetnaclUtil.decodeBase64(senderKeys.sign)))) : message.payload;
const _encrypt = (payload, senderPrivate, receiverPublic) => {
  const sharedKey = tweetnacl.box.before(tweetnaclUtil.decodeBase64(receiverPublic), tweetnaclUtil.decodeBase64(senderPrivate));
  // one time code
  const code = tweetnacl.randomBytes(ONE_TIME_CODE_LENGTH);
  const cipherText = tweetnacl.box.after(tweetnaclUtil.decodeUTF8(JSON.stringify(payload)), code, sharedKey);
  return {
    payload: tweetnaclUtil.encodeBase64(cipherText),
    encryptionData: tweetnaclUtil.encodeBase64(code)
  };
};
const _decrypt = (message, receiverPrivate, senderPublic) => {
  const sharedKey = tweetnacl.box.before(tweetnaclUtil.decodeBase64(senderPublic), tweetnaclUtil.decodeBase64(receiverPrivate));
  const decryptedMessage = tweetnacl.box.open.after(tweetnaclUtil.decodeBase64(message.payload), tweetnaclUtil.decodeBase64(message.encryptionData), sharedKey);
  return decryptedMessage && JSON.parse(tweetnaclUtil.encodeUTF8(decryptedMessage));
};
/**
 * Sign and encrypt message with private and receiver public key
 */
const encrypt = (payload, keys, receiverKeys) => _encrypt(payload, keys.encrypt.private, receiverKeys.encrypt);
/**
 * Decrypt and verify message with private and sender public key
 */
const decrypt = (message, keys, senderKeys) => _decrypt(message, keys.encrypt.private, senderKeys.encrypt);
const encryptState = (payload, keys) => keys.tenantState?.public ? _encrypt(payload, keys.encrypt.private, keys.tenantState.public) : payload;
/**
 * Trial-decrypt a state payload against the candidate keys, in order:
 *   1. per-tenant `tenantState` (current root, preferred when present)
 *   2. per-tenant `tenantStateAux` (prior root, only present mid-rotation)
 *
 * NaCl box authentication makes wrong-key attempts fail cleanly (Poly1305
 * MAC, ~2^-128 false-positive rate). `_decrypt` returns `null` when auth
 * fails, but malformed ciphertext (bad base64, etc.) can still throw —
 * each attempt is wrapped so a bad message on one candidate falls through
 * to the next instead of propagating up.
 *
 * Logs `info` when `tenantStateAux` wins (rotation soak signal — operator
 * waits for this to drain before dropping the prior root from env) and
 * `error` when every candidate fails to authenticate. The legacy shared
 * `state` fallback was removed in PROD-9274 (Phase 3); all peers now
 * encrypt with per-tenant keys.
 */
const decryptState = (message, keys, senderKeys) => {
  if (!keys.tenantState?.private && !keys.tenantStateAux?.private) {
    return message;
  }
  const tryDecrypt = privateKey => {
    try {
      return _decrypt(message, privateKey, senderKeys.encrypt);
    } catch {
      return null;
    }
  };
  if (keys.tenantState?.private) {
    const result = tryDecrypt(keys.tenantState.private);
    if (result !== null) {
      return result;
    }
  }
  if (keys.tenantStateAux?.private) {
    const result = tryDecrypt(keys.tenantStateAux.private);
    if (result !== null) {
      logger$3.info({
        action: 'decryptState',
        message: 'state payload decrypted with tenantStateAux (rotation overlap — peer is still encrypting with the prior root)',
        meta: {
          keyUsed: 'tenantStateAux'
        }
      });
      return result;
    }
  }
  logger$3.error({
    action: 'decryptState',
    message: 'state payload could not be decrypted with any candidate key',
    meta: {
      keyUsed: 'failed'
    }
  });
  return undefined;
};
const deriveBoxPublicKey = privateKeyBase64 => {
  const keyPair = tweetnacl.box.keyPair.fromSecretKey(tweetnaclUtil.decodeBase64(privateKeyBase64));
  return tweetnaclUtil.encodeBase64(keyPair.publicKey);
};
/**
 * Encrypts state for the SAME-role tenant presence channel (e.g.
 * provider↔provider), as opposed to the cross-role room channel that
 * `encryptState` serves.
 *
 * After the Phase 1.5 backend correction (PROD-8787), the server derives
 * `tenantState` cross-role — the public half is the PEER role's, which is what
 * `encryptState` needs for room state. A same-role channel instead needs the
 * SHARED own-role public. We recover it by re-deriving the box public from the
 * own-role PRIVATE half. This is correct regardless of what the server placed
 * in the public half — and so is robust to deploy ordering: the private half is
 * always own-role, so it yields the shared own-role public whether or not the
 * backend swap has shipped yet.
 *
 * NOTE: do NOT collapse this into a bare alias for `encryptState` — that would
 * encrypt presence to the cross-role (peer) public and break same-role
 * (provider↔provider) decryption.
 */
const encryptTenantState = (payload, keys) => {
  if (keys.tenantState?.private) {
    return encryptState(payload, {
      ...keys,
      tenantState: {
        public: deriveBoxPublicKey(keys.tenantState.private),
        private: keys.tenantState.private
      }
    });
  }
  return encryptState(payload, keys);
};

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  decrypt: decrypt,
  decryptState: decryptState,
  deriveBoxPublicKey: deriveBoxPublicKey,
  encrypt: encrypt,
  encryptState: encryptState,
  encryptTenantState: encryptTenantState,
  sign: sign,
  verify: verify
});

// utils functions
// eslint-disable-next-line no-promise-executor-return
const sleep = async (ms = 0) => new Promise(resolve => setTimeout(resolve, ms));
const retryAsync = async (operation, retries = 1, delay = 0, delayMultiplier = 1, errorPredicate = () => true) => {
  try {
    return [await operation(), retries];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error) {
    if (retries > 0 && errorPredicate(error)) {
      await sleep(delay);
      return retryAsync(operation, retries - 1, delay * delayMultiplier, delayMultiplier, errorPredicate);
    }
    if (error) {
      error.retries = retries;
    }
    throw error;
  }
};
const retryAsyncExponential = async (operation, retries = 1, errorPredicate = () => true, exponentStep = 1) => {
  const delay = (2 ** exponentStep - 1) * 1000;
  try {
    return [await operation(), retries];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error) {
    if (retries > 0 && errorPredicate(error)) {
      await sleep(delay);
      return retryAsyncExponential(operation, retries - 1, errorPredicate, exponentStep + 1);
    }
    if (error) {
      error.retries = retries;
    }
    throw error;
  }
};
const getErrorStatus = error => error?.status?.category || error?.status || error?.messages;
const makeOperationWithRetry = async (operation, logger, operationName, options = DEFAULT_RETRY_OPTIONS, errorPredicate) => {
  const errorStatuses = [];
  const errorPredicateWrapper = e => {
    if (!errorPredicate || errorPredicate(e)) {
      errorStatuses.push(getErrorStatus(e));
      return true;
    }
    return false;
  };
  try {
    const [res, retriesLeft] = await (options.isExponential ? retryAsyncExponential(operation, options.retries, errorPredicateWrapper) : retryAsync(operation, options.retries, options.delay, options.delayMultiplier, errorPredicateWrapper));
    if (retriesLeft < options.retries) {
      logger.warn({
        action: 'retry',
        message: 'some of retries was successful',
        meta: {
          retryData: {
            operationName,
            retries: options.retries - retriesLeft,
            errorStatuses
          }
        }
      });
    }
    return res;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error_) {
    const error = error_ || {};
    // error should be logged on consumer side any way, so to not duplicate logs, just return retry data
    // if failed from first try by another reason - not include retryData
    if (options.retries - error_.retries > 0) {
      error.retryData = {
        operationName,
        retries: options.retries - error_.retries,
        errorStatuses: [...errorStatuses, getErrorStatus(error_)]
      };
    }
    throw error;
  }
};

const CH_SEPARATOR = ':';
const NAMESPACE_WILDCARD = '*';
const ROOM_KEY = 'room';
/**
 * Class for generating channel names for realtime
 * @todo in some channels we have user${CH_SEPARATOR}, it seems to be needless, remove it later if safe
 */
class ChannelMaps {
  /**
   * Extracts roomId from channel name
   * @example "room:123:" returns "123"
   * @example "t:890eaa38-ff2e-4e5b-9011-db40306296d1:room:a4b60921-5bd6-4630-a20d-78ba9147c738:" returns "a4b60921-5bd6-4630-a20d-78ba9147c738"
   * @example "user:123:" returns undefined
   */
  static getRoomIdFromChannel(channel) {
    const parts = channel.split(CH_SEPARATOR);
    const roomIndex = parts.indexOf(ROOM_KEY);
    return roomIndex !== -1 ? parts[roomIndex + 1] : undefined;
  }
  /**
   * Returns tenant prefix with the tenantId
   * @example "t:890eaa38-ff2e-4e5b-9011-db40306296d1:"
   */
  static tenantPrefix(tenantId) {
    return tenantId ? `t${CH_SEPARATOR}${tenantId}${CH_SEPARATOR}` : '';
  }
  /**
   * Returns room prefix with the roomId
   * @example "room:123:"
   */
  static roomPrefix(roomId) {
    return `${ROOM_KEY}${CH_SEPARATOR}${roomId}${CH_SEPARATOR}`;
  }
  /**
   * Returns providers presence channel name
   * @example "room:123:providers"
   * @example "t:890eaa38-ff2e-4e5b-9011-db40306296d1:room:a4b60921-5bd6-4630-a20d-78ba9147c738:providers"
   */
  static providersPresence(roomId, tenantId) {
    const tenantPrefix = this.tenantPrefix(tenantId);
    const roomPrefix = this.roomPrefix(roomId);
    return `${tenantPrefix}${roomPrefix}providers`;
  }
  /**
   * Returns patients presence channel name
   * @example "room:123:patients"
   * @example "t:890eaa38-ff2e-4e5b-9011-db40306296d1:room:a4b60921-5bd6-4630-a20d-78ba9147c738:patients"
   */
  static patientsPresence(roomId, tenantId) {
    const tenantPrefix = this.tenantPrefix(tenantId);
    const roomPrefix = this.roomPrefix(roomId);
    return `${tenantPrefix}${roomPrefix}patients`;
  }
  /**
   * Returns provider notifications channel name
   * @example "t:890eaa38-ff2e-4e5b-9011-db40306296d1:room:a4b60921-5bd6-4630-a20d-78ba9147c738:user:providers:PROV1:notifications"
   * @example "room:123:user:providers:PROV1:notifications"
   */
  static providerNotifications(roomId, identityId, tenantId) {
    const tenantPrefix = this.tenantPrefix(tenantId);
    const roomPrefix = this.roomPrefix(roomId);
    return `${tenantPrefix}${roomPrefix}user${CH_SEPARATOR}providers${CH_SEPARATOR}${identityId}${CH_SEPARATOR}notifications`;
  }
  /**
   * Returns patient notifications channel name
   * @example "t:890eaa38-ff2e-4e5b-9011-db40306296d1:room:a4b60921-5bd6-4630-a20d-78ba9147c738:user:patients:PAT1:notifications"
   * @example "room:123:user:patients:PAT1:notifications"
   */
  static patientNotifications(roomId, identityId, tenantId) {
    const tenantPrefix = this.tenantPrefix(tenantId);
    const roomPrefix = this.roomPrefix(roomId);
    return `${tenantPrefix}${roomPrefix}user${CH_SEPARATOR}patients${CH_SEPARATOR}${identityId}${CH_SEPARATOR}notifications`;
  }
  /**
   * Forms a channel to have access to all provider notifications
   * @example "t:890eaa38-ff2e-4e5b-9011-db40306296d1:room:a4b60921-5bd6-4630-a20d-78ba9147c738:user:providers:*:notifications"
   * @example "room:123:user:providers:*:notifications"
   */
  static allProviderNotifications(roomId, tenantId) {
    const tenantPrefix = this.tenantPrefix(tenantId);
    const roomPrefix = this.roomPrefix(roomId);
    return `${tenantPrefix}${roomPrefix}user${CH_SEPARATOR}providers${CH_SEPARATOR}${NAMESPACE_WILDCARD}${CH_SEPARATOR}notifications`;
  }
  /**
   * Forms a channel to have access to all patient notifications
   * @example "t:890eaa38-ff2e-4e5b-9011-db40306296d1:room:a4b60921-5bd6-4630-a20d-78ba9147c738:user:patients:*:notifications"
   * @example "room:123:user:patients:*:notifications"
   */
  static allPatientNotifications(roomId, tenantId) {
    const tenantPrefix = this.tenantPrefix(tenantId);
    const roomPrefix = this.roomPrefix(roomId);
    return `${tenantPrefix}${roomPrefix}user${CH_SEPARATOR}patients${CH_SEPARATOR}${NAMESPACE_WILDCARD}${CH_SEPARATOR}notifications`;
  }
  /**
   * User personal inbox, based on userId, meant for receiving messages from the server
   * @example "user:456"
   */
  static userInbox(userId) {
    return `user${CH_SEPARATOR}${userId}`;
  }
  /**
   * Forms channel to have access to all user inboxes
   * @example "user:*"
   */
  static allUserInboxes() {
    return `user${CH_SEPARATOR}${NAMESPACE_WILDCARD}`;
  }
  /**
   * Returns a channel user needs to enter to trigger presence
   * @example "room:123:providers"
   * @example "room:123:patients"
   */
  static presence(identity, roomId, tenantId) {
    return identity.role === RealtimeRole$1.Provider ? this.providersPresence(roomId, tenantId) : this.patientsPresence(roomId, tenantId);
  }
  /**
   * Returns a channel user needs to subscribe to receive presence updates
   * @example "room:123:patients"
   * @example "room:123:providers"
   */
  static presenceSubscribable(identity, roomId, tenantId) {
    return identity.role === RealtimeRole$1.Provider ? this.patientsPresence(roomId, tenantId) : this.providersPresence(roomId, tenantId);
  }
  /**
   * Returns tenant providers presence channel name
   * It's used to see who is online in the tenant providers
   * @example "t:890eaa38-ff2e-4e5b-9011-db40306296d1:pr"
   */
  static tenantProvidersPresence(tenantId) {
    return `${this.tenantPrefix(tenantId)}pr`;
  }
  /**
   * Returns a channel user needs to subscribe to receive notifications
   * @example "t:890eaa38-ff2e-4e5b-9011-db40306296d1:room:a4b60921-5bd6-4630-a20d-78ba9147c738:user:providers:PROV1:notifications"
   * @example "room:123:user:patients:PAT1:notifications"
   */
  static notifications(identity, roomId, tenantId) {
    return identity.role === RealtimeRole$1.Provider ? this.providerNotifications(roomId, identity.id, tenantId) : this.patientNotifications(roomId, identity.id, tenantId);
  }
}

/** The type of additional information */
var AdditionalInformationType;
(function (AdditionalInformationType) {
  AdditionalInformationType["Image"] = "Image";
  AdditionalInformationType["Text"] = "Text";
  AdditionalInformationType["Video"] = "Video";
})(AdditionalInformationType || (AdditionalInformationType = {}));
var CallStartSound;
(function (CallStartSound) {
  CallStartSound["Chime"] = "Chime";
  CallStartSound["Doorbell"] = "Doorbell";
  CallStartSound["TaDa"] = "TaDa";
  CallStartSound["WaitingFor"] = "WaitingFor";
})(CallStartSound || (CallStartSound = {}));
var Category;
(function (Category) {
  Category["Library"] = "Library";
  Category["Prototype"] = "Prototype";
})(Category || (Category = {}));
var ClientExtensionState;
(function (ClientExtensionState) {
  ClientExtensionState["Activated"] = "Activated";
  ClientExtensionState["Disabled"] = "Disabled";
})(ClientExtensionState || (ClientExtensionState = {}));
var FileMetadataErrorCode;
(function (FileMetadataErrorCode) {
  FileMetadataErrorCode["FileNotFound"] = "FileNotFound";
  FileMetadataErrorCode["Unknown"] = "Unknown";
})(FileMetadataErrorCode || (FileMetadataErrorCode = {}));
var FronteggExtensionsFeatureKeys;
(function (FronteggExtensionsFeatureKeys) {
  FronteggExtensionsFeatureKeys["AppsAgenda"] = "AppsAgenda";
  FronteggExtensionsFeatureKeys["AppsAssessments"] = "AppsAssessments";
  FronteggExtensionsFeatureKeys["AppsBundleFileTransferPhotoCapture"] = "AppsBundleFileTransferPhotoCapture";
  FronteggExtensionsFeatureKeys["AppsCbtActivities"] = "AppsCbtActivities";
  FronteggExtensionsFeatureKeys["AppsDialInBeta"] = "AppsDialInBeta";
  FronteggExtensionsFeatureKeys["AppsEmdr"] = "AppsEmdr";
  FronteggExtensionsFeatureKeys["AppsFax"] = "AppsFax";
  FronteggExtensionsFeatureKeys["AppsFileTransfer"] = "AppsFileTransfer";
  FronteggExtensionsFeatureKeys["AppsGames"] = "AppsGames";
  FronteggExtensionsFeatureKeys["AppsInAppAnnouncementsBeta"] = "AppsInAppAnnouncementsBeta";
  FronteggExtensionsFeatureKeys["AppsInterpreter"] = "AppsInterpreter";
  FronteggExtensionsFeatureKeys["AppsIssueReportBeta"] = "AppsIssueReportBeta";
  FronteggExtensionsFeatureKeys["AppsMeetRedirectBeta"] = "AppsMeetRedirectBeta";
  FronteggExtensionsFeatureKeys["AppsNotepad"] = "AppsNotepad";
  FronteggExtensionsFeatureKeys["AppsPayment"] = "AppsPayment";
  FronteggExtensionsFeatureKeys["AppsPhotoCapture"] = "AppsPhotoCapture";
  FronteggExtensionsFeatureKeys["AppsRecord"] = "AppsRecord";
  FronteggExtensionsFeatureKeys["AppsReference"] = "AppsReference";
  FronteggExtensionsFeatureKeys["AppsSchedulerBeta"] = "AppsSchedulerBeta";
  FronteggExtensionsFeatureKeys["AppsScribe"] = "AppsScribe";
  FronteggExtensionsFeatureKeys["AppsTeleconsent"] = "AppsTeleconsent";
  FronteggExtensionsFeatureKeys["AppsTimer"] = "AppsTimer";
  FronteggExtensionsFeatureKeys["AppsTranscript"] = "AppsTranscript";
  FronteggExtensionsFeatureKeys["AppsWhiteboard"] = "AppsWhiteboard";
  FronteggExtensionsFeatureKeys["BetaAccessFull"] = "BetaAccessFull";
  FronteggExtensionsFeatureKeys["UnpublishedManifests"] = "UnpublishedManifests";
})(FronteggExtensionsFeatureKeys || (FronteggExtensionsFeatureKeys = {}));
var Iso;
(function (Iso) {
  Iso["Ar"] = "ar";
  Iso["De"] = "de";
  Iso["El"] = "el";
  Iso["En"] = "en";
  Iso["Es"] = "es";
  Iso["Fi"] = "fi";
  Iso["Fr"] = "fr";
  Iso["Hi"] = "hi";
  Iso["Hr"] = "hr";
  Iso["It"] = "it";
  Iso["Ja"] = "ja";
  Iso["Ko"] = "ko";
  Iso["Nl"] = "nl";
  Iso["Pl"] = "pl";
  Iso["Pt"] = "pt";
  Iso["Ro"] = "ro";
  Iso["Ru"] = "ru";
  Iso["Sv"] = "sv";
  Iso["Uk"] = "uk";
  Iso["Ur"] = "ur";
  Iso["Vi"] = "vi";
  Iso["ZhCn"] = "zh_CN";
  Iso["ZhTw"] = "zh_TW";
})(Iso || (Iso = {}));
/** The content type of the image */
var ImageType;
(function (ImageType) {
  ImageType["Ico"] = "Ico";
  ImageType["Jpeg"] = "Jpeg";
  ImageType["Jpg"] = "Jpg";
  ImageType["Png"] = "Png";
})(ImageType || (ImageType = {}));
/** Type of membership for a room: 'owner' (personal room for user), 'guest' (member of someone else's personal room), or 'shared' (member of a shared room) */
var MembershipType;
(function (MembershipType) {
  MembershipType["Guest"] = "Guest";
  MembershipType["Owner"] = "Owner";
  MembershipType["Shared"] = "Shared";
})(MembershipType || (MembershipType = {}));
/** The patient instruction type to use for the heading */
var PatientInstructionsHeading;
(function (PatientInstructionsHeading) {
  PatientInstructionsHeading["Announcement"] = "Announcement";
  PatientInstructionsHeading["MakeMostOfVisit"] = "MakeMostOfVisit";
  PatientInstructionsHeading["PreAppointment"] = "PreAppointment";
  PatientInstructionsHeading["WhatToExpect"] = "WhatToExpect";
})(PatientInstructionsHeading || (PatientInstructionsHeading = {}));
/** The professional information type to use for the heading */
var ProfessionalInformationHeading;
(function (ProfessionalInformationHeading) {
  ProfessionalInformationHeading["AboutMe"] = "AboutMe";
  ProfessionalInformationHeading["GetToKnow"] = "GetToKnow";
})(ProfessionalInformationHeading || (ProfessionalInformationHeading = {}));
var ProviderFeatureKey;
(function (ProviderFeatureKey) {
  ProviderFeatureKey["Branding"] = "Branding";
  ProviderFeatureKey["Premium"] = "Premium";
  ProviderFeatureKey["ProPlan"] = "ProPlan";
})(ProviderFeatureKey || (ProviderFeatureKey = {}));
/** The role of the client in the realtime session */
var RealtimeRole;
(function (RealtimeRole) {
  RealtimeRole["Auditor"] = "Auditor";
  RealtimeRole["Patient"] = "Patient";
  RealtimeRole["Provider"] = "Provider";
  RealtimeRole["Spectator"] = "Spectator";
  RealtimeRole["System"] = "System";
})(RealtimeRole || (RealtimeRole = {}));
/** The Reception type to use for the heading */
var ReceptionHeading;
(function (ReceptionHeading) {
  ReceptionHeading["GenericWelcome"] = "GenericWelcome";
  ReceptionHeading["PersonalisedWithName"] = "PersonalisedWithName";
})(ReceptionHeading || (ReceptionHeading = {}));
/** Platform for session (web or mobile only) */
var SessionPlatformInput;
(function (SessionPlatformInput) {
  SessionPlatformInput["Mobile"] = "mobile";
  SessionPlatformInput["Web"] = "web";
})(SessionPlatformInput || (SessionPlatformInput = {}));
var Status;
(function (Status) {
  Status["Active"] = "Active";
  Status["Beta"] = "Beta";
  Status["Inactive"] = "Inactive";
})(Status || (Status = {}));
/** Type of provider subscription */
var UserSubscriptionType;
(function (UserSubscriptionType) {
  UserSubscriptionType["Clinic"] = "clinic";
  UserSubscriptionType["Free"] = "free";
  UserSubscriptionType["Premium"] = "premium";
  UserSubscriptionType["Pro"] = "pro";
  UserSubscriptionType["Trial"] = "trial";
})(UserSubscriptionType || (UserSubscriptionType = {}));
/** The call session type */
var WebRtcSessionType;
(function (WebRtcSessionType) {
  WebRtcSessionType["P2P"] = "P2P";
  WebRtcSessionType["Routed"] = "Routed";
})(WebRtcSessionType || (WebRtcSessionType = {}));
class TypedDocumentString extends String {
  __apiType;
  value;
  __meta__;
  constructor(value, __meta__) {
    super(value);
    this.value = value;
    this.__meta__ = __meta__;
  }
  toString() {
    return this.value;
  }
}
const RealtimeJoinDocument = new TypedDocumentString(`
    mutation RealtimeJoin($input: RealtimeJoinInput!) {
  Realtime {
    join(input: $input) {
      roomIds
      token
      tenantId
      identity {
        id
        role
        userId
      }
      keys {
        encrypt {
          private
          public
        }
        sign {
          private
          public
        }
        tenantState {
          private
          public
        }
        tenantStateAux {
          private
          public
        }
      }
    }
  }
}
    `);
const RealtimeLeaveDocument = new TypedDocumentString(`
    mutation RealtimeLeave($input: RealtimeLeaveInput!) {
  Realtime {
    leave(input: $input)
  }
}
    `);
const UploadProfilePictureLinkDocument = new TypedDocumentString(`
    mutation UploadProfilePictureLink($input: UploadProfilePictureLinkInput!) {
  Realtime {
    uploadProfilePictureLink(input: $input) {
      uuid
      uploadLink
    }
  }
}
    `);
const FetchProfilePictureDocument = new TypedDocumentString(`
    mutation FetchProfilePicture($fileUuid: String!) {
  Realtime {
    fetchProfilePicture(fileUuid: $fileUuid) {
      uuid
      filename
      expiresOn
      status
      downloadLink
      tags
    }
  }
}
    `);
const UploadProfilePictureDocument = new TypedDocumentString(`
    mutation UploadProfilePicture($input: UploadProfilePictureInput!) {
  Realtime {
    uploadProfilePicture(input: $input)
  }
}
    `);
const GenerateTokenRequestDocument = new TypedDocumentString(`
    mutation GenerateTokenRequest {
  Realtime {
    generateTokenRequest {
      keyName
      clientId
      capability
      timestamp
      nonce
      mac
    }
  }
}
    `);
const RefreshTokenDocument = new TypedDocumentString(`
    mutation RefreshToken {
  Realtime {
    refreshToken {
      token
    }
  }
}
    `);

/* eslint-disable */
/**
 * Map of all GraphQL operations in the project.
 *
 * This map has several performance disadvantages:
 * 1. It is not tree-shakeable, so it will include all operations in the project.
 * 2. It is not minifiable, so the string of a GraphQL query will be multiple times inside the bundle.
 * 3. It does not support dead code elimination, so it will add unused operations.
 *
 * Therefore it is highly recommended to use the babel or swc plugin for production.
 */
const documents = {
  "\n  mutation RealtimeJoin($input: RealtimeJoinInput!) {\n    Realtime {\n      join(input: $input) {\n        roomIds\n        token\n        tenantId\n        identity {\n          id\n          role\n          userId\n        }\n        keys {\n          encrypt {\n            private\n            public\n          }\n          sign {\n            private\n            public\n          }\n          tenantState {\n            private\n            public\n          }\n          tenantStateAux {\n            private\n            public\n          }\n        }\n      }\n    }\n  }\n": RealtimeJoinDocument,
  "\n  mutation RealtimeLeave($input: RealtimeLeaveInput!) {\n    Realtime {\n      leave(input: $input)\n    }\n  }\n": RealtimeLeaveDocument,
  "\n  mutation UploadProfilePictureLink($input: UploadProfilePictureLinkInput!) {\n    Realtime {\n      uploadProfilePictureLink(input: $input) {\n        uuid\n        uploadLink\n      }\n    }\n  }\n": UploadProfilePictureLinkDocument,
  "\n  mutation FetchProfilePicture($fileUuid: String!) {\n    Realtime {\n      fetchProfilePicture(fileUuid: $fileUuid) {\n        uuid\n        filename\n        expiresOn\n        status\n        downloadLink\n        tags\n      }\n    }\n  }\n": FetchProfilePictureDocument,
  "\n  mutation UploadProfilePicture($input: UploadProfilePictureInput!) {\n    Realtime {\n      uploadProfilePicture(input: $input)\n    }\n  }\n": UploadProfilePictureDocument,
  "\n  mutation GenerateTokenRequest {\n    Realtime {\n      generateTokenRequest {\n        keyName\n        clientId\n        capability\n        timestamp\n        nonce\n        mac\n      }\n    }\n  }\n": GenerateTokenRequestDocument,
  "\n  mutation RefreshToken {\n    Realtime {\n      refreshToken {\n        token\n      }\n    }\n  }\n": RefreshTokenDocument
};
function graphql(source) {
  return documents[source] ?? {};
}

const JoinMutation = graphql(`
  mutation RealtimeJoin($input: RealtimeJoinInput!) {
    Realtime {
      join(input: $input) {
        roomIds
        token
        tenantId
        identity {
          id
          role
          userId
        }
        keys {
          encrypt {
            private
            public
          }
          sign {
            private
            public
          }
          tenantState {
            private
            public
          }
          tenantStateAux {
            private
            public
          }
        }
      }
    }
  }
`);
const LeaveMutation = graphql(`
  mutation RealtimeLeave($input: RealtimeLeaveInput!) {
    Realtime {
      leave(input: $input)
    }
  }
`);
const UploadPictureLinkMutation = graphql(`
  mutation UploadProfilePictureLink($input: UploadProfilePictureLinkInput!) {
    Realtime {
      uploadProfilePictureLink(input: $input) {
        uuid
        uploadLink
      }
    }
  }
`);
const FetchProfilePictureMutation = graphql(`
  mutation FetchProfilePicture($fileUuid: String!) {
    Realtime {
      fetchProfilePicture(fileUuid: $fileUuid) {
        uuid
        filename
        expiresOn
        status
        downloadLink
        tags
      }
    }
  }
`);
graphql(`
  mutation UploadProfilePicture($input: UploadProfilePictureInput!) {
    Realtime {
      uploadProfilePicture(input: $input)
    }
  }
`);
const GenerateTokenRequestMutation = graphql(`
  mutation GenerateTokenRequest {
    Realtime {
      generateTokenRequest {
        keyName
        clientId
        capability
        timestamp
        nonce
        mac
      }
    }
  }
`);
const RefreshTokenMutation = graphql(`
  mutation RefreshToken {
    Realtime {
      refreshToken {
        token
      }
    }
  }
`);

class Member {
  identity;
  roomId;
  _state;
  _picture;
  _connectionId;
  constructor(identity, roomId, state, picture, connectionId) {
    this.roomId = roomId;
    this.identity = identity;
    this.update(state, picture, connectionId);
  }
  /**
   * Get identity id
   */
  get id() {
    return this.identity.id;
  }
  /**
   * Get state
   */
  get state() {
    return this._state?.payload ?? {};
  }
  /**
   * Get whole state with internal props and payload
   */
  get stateWrapper() {
    return this._state ?? {};
  }
  /**
   * Get state
   */
  get picture() {
    return this._picture;
  }
  get connectionId() {
    return this._connectionId;
  }
  /**
   * Get actual realtime member object
   */
  get item() {
    return {
      identity: this.identity,
      roomId: this.roomId,
      state: this.state,
      picture: this._picture,
      connectionId: this._connectionId
    };
  }
  /**
   * If has a state at the moment
   */
  get hasState() {
    return Object.keys(this.state).length > 0;
  }
  // presence related
  /**
   * Update member state and picture
   */
  update(state, picture, connectionId, isHard = false) {
    this._state = state || !isHard && this._state || undefined;
    this._picture = picture || !isHard && this._picture || undefined;
    this._connectionId = connectionId || !isHard && this._connectionId || undefined;
    return this;
  }
}

class MembersCollection {
  _memberInstancesMap = {};
  constructor(membersMap = {}) {
    this._memberInstancesMap = membersMap;
  }
  /**
   * Returns room or tenant members map who is currently joined and has state
   */
  get membersMap() {
    return this._memberInstancesMap;
  }
  /**
   * Get actual realtime room object
   */
  get item() {
    return {
      membersMap: mapValues(this.membersMap, member => member.item)
    };
  }
  /**
   * Update members map, go through membersMap, check if member exists - update it, otherwise - add new.
   */
  update(membersMap, isReset = true) {
    const newMembersMap = mapValues(membersMap, member => this.membersMap[member.identity.id]?.update(member.stateWrapper, member.picture, member.connectionId) || member);
    if (isReset) {
      for (const id of difference(Object.keys(this.membersMap), Object.keys(newMembersMap))) {
        delete this.membersMap[id];
      }
    }
    this._memberInstancesMap = {
      ...this.membersMap,
      ...newMembersMap
    };
    return this;
  }
  /**
   * Add member if does not exist, otherwise - keep existing as it is
   */
  addMember(member) {
    this.membersMap[member.id] = this.membersMap[member.id] || member;
    return this;
  }
  /**
   * Add member if does not exist, otherwise - update existing
   */
  updateMember(member) {
    this.membersMap[member.id] = this.membersMap[member.id]?.update(member.stateWrapper, member.picture, member.connectionId, true) || member;
    return this;
  }
  /**
   * Remove member from map
   */
  removeMember(member) {
    delete this.membersMap[member.id];
    return this;
  }
}

class Room extends MembersCollection {
  id;
  constructor(id, membersMap = {}) {
    super(membersMap);
    this.id = id;
  }
  get item() {
    return {
      id: this.id,
      ...super.item
    };
  }
}

function isRealtimeEncryptedStateWrapper(state) {
  return state?.payload?.encryptionData !== undefined;
}
function isFileData(file) {
  return file.data !== undefined;
}

/**
 * This is a wrapper around fetch to make graphql requests
 * @param {string} backendURL
 * @param query
 * @param options
 */
async function graphqlExecute(backendURL, query, {
  variables,
  headers = {}
} = {}, setCredentials = false) {
  const response = await fetch(backendURL, {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': 'application/json',
      Accept: 'application/graphql-response+json',
      'x-realtime-client-version': version
    },
    keepalive: true,
    body: JSON.stringify({
      query,
      variables
    }),
    credentials: setCredentials ? 'include' : 'omit'
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  const {
    data,
    errors
  } = await response.json();
  if (errors) {
    throw new Error('Error in GraphQL request');
  }
  return data;
}

let logger$2 = new ClientLogger({
  title: 'AbstractRealtime'
});
/**
 * Base class, we extends from it when implement provider specific class
 * It should include all the public methods, abstract with implementation,
 * because only this class we open for external usage, no one should know about provider specific classes
 */
class AbstractRealtime {
  backendURL;
  getVendorLogLevel;
  _token;
  _keys;
  _identity;
  _listeners = [];
  _roomIds = [];
  _state = {};
  _picture;
  _roomsMap = {};
  _tenantId;
  _tenant;
  constructor(backendURL = '', getVendorLogLevel) {
    this.backendURL = backendURL;
    this.getVendorLogLevel = getVendorLogLevel;
  }
  /**
   * Join as provider. Only a token is required; user identity is derived
   * from the token on the backend.
   */
  async joinAsProvider(roomIds, credentials, state) {
    return this.join(RealtimeRole$1.Provider, roomIds, credentials, state);
  }
  /**
   * Join as patient, has patient specific params - role, room passcode
   * Call join method from child (specific provider) class
   */
  async joinAsPatient(roomId, credentials, state) {
    return this.join(RealtimeRole$1.Patient, [roomId], credentials, state);
  }
  /**
   * Join as server, has server specific params - role, our token
   * Call join method from child (specific provider) class
   */
  async joinAsSystem(credentials) {
    return this.join(RealtimeRole$1.System, [], credentials);
  }
  /**
   * Join the tenant presence channel. Call after join() when you need tenant-scoped
   * presence (e.g. active providers in the tenant). Safe to call multiple times.
   * Only has effect for providers with a tenantId.
   */
  async joinTenantChannel() {
    // No-op in base; overridden by provider implementations (e.g. Ably).
  }
  /**
   * Add event listeners, can be multiple events per one handler
   */
  on(type, cb) {
    const events = type === '*' ? Object.values(RealtimeEventType) : [...type];
    this._listeners.push({
      events,
      cb
    });
    return () => {
      const index = this._listeners.findIndex(l => l.events === events && l.cb === cb);
      this._listeners.splice(index, 1);
    };
  }
  /**
   * Identity getter, we allow only to get this value, not to set.
   */
  get identity() {
    return this._identity || {};
  }
  /**
   * Get rooms map
   */
  get roomsMap() {
    return mapValues(this._roomsMap, room => room.item);
  }
  /**
   * Get all members online map, will be opposite to identity role, for provider - patients, etc.
   */
  get membersMap() {
    return mapValues(this._membersMap, member => member.item);
  }
  get tenantMembersMap() {
    return this._tenant?.item.membersMap ?? {};
  }
  /**
   * Get members by user id map, allow us to get doxyme users (as keys) and realtime members array as value
   */
  get membersByUserId() {
    return groupBy(Object.values(this.membersMap), member => member.identity.userId || member.identity.id);
  }
  /**
   * Call join api endpoint, set isJoined to true, to not call leave if not joined, save credentials we receive.
   * set realtime access token to api, so child classes do not care about it, we return response without token.
   */
  async serverJoin(role, roomIds, credentials = {}) {
    logger$2 = logger$2.bind({
      meta: {
        identity: {
          role
        }
      }
    });
    const response = await this._serverJoin(role, roomIds, credentials);
    this._token = response.token;
    this._identity ??= Object.freeze(response.identity);
    this._keys ??= response.keys;
    this._roomIds = response.roomIds;
    this._tenantId = response.tenantId;
    logger$2 = logger$2.bind({
      meta: {
        identity: this.identity
      }
    });
    logger$2.debug({
      action: 'serverJoin',
      message: 'success',
      meta: {
        roomIds: response.roomIds,
        tenantId: response.tenantId
      }
    });
    return response;
  }
  async _serverJoin(role, roomIds, credentials = {}) {
    try {
      const response = await makeOperationWithRetry(async () => this.checkTokenAndGraphqlExecute(JoinMutation, {
        input: {
          role,
          roomIds,
          credentials
        }
      }, true), logger$2, 'serverJoin');
      return response?.Realtime.join;
    } catch (error) {
      logger$2.error({
        action: 'serverJoin',
        message: error,
        meta: {
          roomIds,
          errorStack: getErrorStack(error)
        }
      });
      throw error;
    }
  }
  /**
   * Upload profile picture to server and get download link.
   */
  async uploadProfilePicture(uploadData, file) {
    let downloadLink = '';
    try {
      const {
        Realtime: {
          uploadProfilePictureLink
        }
      } = await this.getPictureUploadLink(uploadData);
      if (!uploadProfilePictureLink) {
        throw new RealtimeError('Failed to get upload link');
      }
      const {
        uploadLink,
        uuid
      } = uploadProfilePictureLink;
      const data = file.data;
      const headers = {
        'Content-Type': uploadData.contentType
      };
      await put(uploadLink, data, headers);
      logger$2.debug({
        action: 'uploadProfilePicture',
        message: 'success',
        meta: {
          uuid
        }
      });
      downloadLink = await this.pollForPictureDownloadLink(uuid);
    } catch (error) {
      logger$2.error({
        action: 'uploadProfilePicture',
        message: error?.message,
        meta: {
          errorStack: getErrorStack(error),
          downloadLink
        }
      });
    }
    return downloadLink;
  }
  /**
   * Send file metadata to the server and obtain the upload link for file upload.
   */
  async getPictureUploadLink(uploadData) {
    const response = await makeOperationWithRetry(async () => this.checkTokenAndGraphqlExecute(UploadPictureLinkMutation, {
      input: uploadData
    }), logger$2, 'getPictureUploadLink');
    return response;
  }
  /**
   * Retrieve the download link for a given file UUID.
   *
   * Since the download link might not be immediately available after upload, we retry until it becomes accessible.
   */
  async pollForPictureDownloadLink(fileUuid, maxRetries = 10, delay = 2000, retries = 0) {
    // Token might be missing if the user has left the session before the download link was available
    if (!this._token) {
      return '';
    }
    if (retries >= maxRetries) {
      throw new RealtimeError('Failed to fetch profile picture');
    }
    const response = await makeOperationWithRetry(async () => this.checkTokenAndGraphqlExecute(FetchProfilePictureMutation, {
      fileUuid
    }), logger$2, 'pollForPictureDownloadLink');
    if (response?.Realtime.fetchProfilePicture?.downloadLink) {
      return response.Realtime.fetchProfilePicture.downloadLink;
    }
    await sleep(delay);
    return this.pollForPictureDownloadLink(fileUuid, maxRetries, delay, retries + 1);
  }
  /**
   * Call leave api endpoint to cleanup server side, set isJoined to false
   */
  async serverLeave(input = {}) {
    if (!this._token) {
      return;
    }
    await makeOperationWithRetry(async () => this.checkTokenAndGraphqlExecute(LeaveMutation, {
      input
    }), logger$2, 'serverLeave');
    this._token = '';
    this._identity = undefined;
    this._keys = undefined;
    this._state = {};
    this._picture = undefined;
    this._roomIds = [];
    this._roomsMap = {};
    this._tenantId = undefined;
    logger$2 = logger$2.bind({
      meta: {
        identity: null
      }
    });
  }
  /**
   * Call GraphQL refreshToken api endpoint to refresh token before expire
   */
  async refreshRealtimeToken(id, roomIds) {
    try {
      const response = await graphqlExecute(this.backendURL, RefreshTokenMutation, {
        variables: {
          input: {
            id,
            roomIds
          }
        }
      }, true);
      const token = response?.Realtime.refreshToken.token;
      if (token) {
        this._token = token;
      }
    } catch (error) {
      logger$2.error({
        action: 'refreshToken',
        message: error,
        meta: {
          roomIds,
          errorStack: getErrorStack(error)
        }
      });
      throw error;
    }
  }
  decodeAccessToken(token) {
    let decoded;
    const base64Url = token.split('.')[1];
    if (base64Url) {
      decoded = JSON.parse(atob(base64Url.replace(/-/g, '+').replace(/_/g, '/')));
    }
    return decoded;
  }
  async checkTokenAndGraphqlExecute(query, variables, setCredentials = false) {
    try {
      if (this._token) {
        const decodedToken = this.decodeAccessToken(this._token);
        if (!decodedToken) {
          throw new Error('Token not valid!');
        }
        if (decodedToken?.exp) {
          const currentTime = Date.now() / 1000; // Convert milliseconds to seconds
          const expiresSoonThreshold = 60 * 60; // 1 hour
          if (decodedToken.exp - currentTime < expiresSoonThreshold) {
            await this.refreshRealtimeToken(decodedToken.id, decodedToken.roomIds);
          }
        }
      }
      return graphqlExecute(this.backendURL, query, {
        variables,
        headers: this._token ? {
          Authorization: this._token
        } : {}
      }, setCredentials);
    } catch (error) {
      logger$2.error({
        action: 'checkTokenAndGraphqlExecute',
        message: JSON.stringify(error),
        meta: {
          variables,
          errorStack: getErrorStack(error),
          error
        }
      });
      throw error;
    }
  }
  /**
   * Fire listener for specific events async
   */
  fire(events) {
    setTimeout(() => {
      for (const event of events) {
        for (const l of this._listeners) {
          if (l.events.includes(event.type)) {
            l.cb(event, event.type);
          }
        }
      }
    });
  }
  /**
   * Return roomIds, just getter as we set it only from server response
   */
  get roomIds() {
    return this._roomIds;
  }
  /**
   * Return tenantId, just getter as we set it only from server response
   */
  get tenantId() {
    return this._tenantId;
  }
  /**
   * Get room ids with people connected (not empty)
   */
  get roomIdsWithMembers() {
    return this.identity.role === RealtimeRole$1.Provider ? this.membersRoomIds : [this.roomIds[0]];
  }
  /**
   * Get roomIds where we have members
   */
  get membersRoomIds() {
    return Object.values(this._roomsMap).filter(room => Object.keys(room.membersMap).length > 0).map(room => room.id);
  }
  /**
   * Get all members map, but with whole state, including internal props
   */
  get _membersMap() {
    return Object.values(this._roomsMap).reduce((membersMap, room) => ({
      ...membersMap,
      ...room.membersMap
    }), {});
  }
  /**
   * Get all keys
   */
  get allKeys() {
    return this._keys || {
      encrypt: {
        private: '',
        public: ''
      }
    };
  }
  /**
   * Get only public keys
   */
  get publicKeys() {
    return {
      sign: this.allKeys.sign?.public,
      encrypt: this.allKeys.encrypt.public
    };
  }
  /**
   * Return token
   */
  get token() {
    return this._token;
  }
  /**
   * Helper method to get keys from state
   */
  getKeysFromState(state) {
    return state?.__internal?.keys ?? {
      encrypt: ''
    };
  }
  /**
   * Check target keys, if keys were not set - throw error
   */
  getMembersKeys(identity) {
    const memberKeys = this.getKeysFromState(this._membersMap[identity.id]?.stateWrapper);
    if (!memberKeys?.encrypt) {
      throw new RealtimeError('We can not encrypt/decrypt message because sender/receiver member does not have encryption keys in state');
    }
    return memberKeys;
  }
}

const logger$1 = new ClientLogger({
  title: 'ConnectionManager'
});
const logError$1 = logError$2.bind(null, logger$1);
const MAX_CHANNELS_PER_CONNECTION = 1000;
/**
 * Manages multiple Ably connections to handle the channel limit per connection.
 * Distributes channels across connections using a round-robin approach.
 */
class ConnectionManager {
  onConnectionStateChange;
  ablyConfig;
  connections = [];
  channelToConnectionMap = new Map();
  createConnectionPromise;
  constructor(onConnectionStateChange) {
    this.onConnectionStateChange = onConnectionStateChange;
  }
  /**
   * Initialize the connection manager with Ably configuration
   */
  async initialize(ablyConfig) {
    if (this.isInitialized()) {
      return;
    }
    this.ablyConfig = ablyConfig;
    // Create the first connection
    await this.createConnection();
  }
  /**
   * Get a channel from the appropriate connection
   */
  async getChannel(channelName) {
    // Check if channel already exists in a connection
    const existingConnection = this.channelToConnectionMap.get(channelName);
    if (existingConnection) {
      return existingConnection.instance.channels.get(channelName);
    }
    // Find a connection with available capacity
    let targetConnection = this.connections.find(connection => connection.channels.size < MAX_CHANNELS_PER_CONNECTION);
    // If no connection has capacity, create a new one
    if (!targetConnection) {
      try {
        targetConnection = await this.createConnection();
      } catch (error) {
        logError$1(error, 'getChannel', {
          channelName,
          connections: this.connections
        });
        return undefined;
      }
    }
    targetConnection.channels.add(channelName);
    this.channelToConnectionMap.set(channelName, targetConnection);
    return targetConnection.instance.channels.get(channelName);
  }
  /**
   * Release a channel: detach from Ably and remove from tracking.
   * This frees up the channel slot for the connection limit.
   */
  async releaseChannel(channelName) {
    const connection = this.channelToConnectionMap.get(channelName);
    if (!connection) {
      return;
    }
    try {
      const channel = connection.instance.channels.get(channelName);
      if (channel) {
        await channel.detach();
        connection.instance.channels.release(channelName);
        // Remove from tracking
        connection.channels.delete(channelName);
        this.channelToConnectionMap.delete(channelName);
      }
    } catch (error) {
      logError$1(error, 'releaseChannel', {
        channelName
      });
    }
  }
  /**
   * Close all connections
   */
  close() {
    this.connections.forEach(({
      instance
    }) => instance.close());
    this.connections = [];
    this.channelToConnectionMap.clear();
  }
  /**
   * If the connection manager is initialized, basically if there is a primary connection
   */
  isInitialized() {
    return !!this.connections[0]?.instance;
  }
  /**
   * If all connections are in connected state, false if there are no connections
   */
  isConnected() {
    return this.connections.length > 0 && this.connections.every(connection => connection.instance.connection.state === 'connected');
  }
  /**
   * Return the ids of all connections
   */
  getConnectionIds() {
    return this.connections.map(connection => connection.instance.connection.id);
  }
  /**
   * Authorize the last connection,
   * last because it is the one that could be affected by new permissions
   */
  async authorizeWithNewPermissions() {
    const lastConnection = this.connections[this.connections.length - 1];
    await lastConnection?.instance.auth.authorize();
  }
  /**
   * Create a new Ably connection
   */
  async createConnection() {
    return this.createConnectionPromise ??= (async () => {
      try {
        if (!this.ablyConfig) {
          throw new Error('Ably config is not defined');
        }
        const {
          Realtime
        } = await import('ably');
        const instance = new Realtime(this.ablyConfig);
        instance.connection.on(state => this.onConnectionStateChange(state, instance.connection.id));
        await instance.connection.once('connected');
        const connection = {
          instance,
          channels: new Set()
        };
        this.connections.push(connection);
        return connection;
      } catch (error) {
        throw logError$1(error, 'createConnection');
      } finally {
        this.createConnectionPromise = undefined;
      }
    })();
  }
}

class Tenant extends MembersCollection {}

let logger = new ClientLogger({
  title: 'Realtime'
});
const logError = logError$2.bind(null, logger);
class Realtime extends AbstractRealtime {
  connectionManager;
  joinPromise = null;
  subscribedChannels = new Set();
  isJoinTenantChannelInProgress = false;
  hasJoinedTenantChannel = false;
  /**
   * Serializes all `_state` mutations (`setState`, `updateState`) so that
   * read-merge-write sequences can't interleave with each other or with
   * `_join`'s state assignment.
   */
  stateMutationChain = Promise.resolve();
  constructor(backendURL = '', getVendorLogLevel) {
    super(backendURL, getVendorLogLevel);
    this.connectionManager = new ConnectionManager(this.onConnectionStateChange.bind(this));
    clientSideCode(windowObj => {
      windowObj.addEventListener('pagehide', event => {
        if (!event.persisted) {
          this.leave(true);
        }
      });
    });
  }
  async join(role, roomIds, credentials = {}, state) {
    return this.joinPromise ??= this._join(role, roomIds, credentials, state);
  }
  async _join(role, roomIds, credentials = {}, state) {
    let isFirstJoin = false; // to keep track if this is the first join
    // to have data in meta if something happens before join, but only if not joined yet
    if (!this.identity.id) {
      isFirstJoin = true;
      logger = logger.bind({
        meta: {
          identity: {
            role
          }
        }
      });
    }
    // Capture current rooms BEFORE serverJoin updates them
    const previousRoomIds = [...this.roomIds];
    try {
      const {
        roomIds: newRoomIds
      } = await this.serverJoin(role, roomIds, credentials);
      logger = logger.bind({
        meta: {
          identity: this.identity
        }
      });
      await this.initConnectionManager();
      // Calculate room changes
      const roomsToJoin = difference(newRoomIds, previousRoomIds);
      const roomsToLeave = difference(previousRoomIds, newRoomIds);
      // If no changes, nothing to do
      if (roomsToJoin.length === 0 && roomsToLeave.length === 0) {
        return this;
      }
      // Unsubscribe from rooms we're leaving
      if (roomsToLeave.length > 0) {
        await Promise.all(roomsToLeave.map(roomId => this.leaveRoom(roomId)));
      }
      // authorize should not be called the first time you connect,
      // as this will cause a duplicate call to generateAblyTokenRequest.
      if (!isFirstJoin) {
        await this.connectionManager.authorizeWithNewPermissions();
      }
      this._state = {
        payload: state,
        __internal: this.getInternalState()
      };
      // Subscribe to new rooms only
      if (roomsToJoin.length > 0) {
        await Promise.all(roomsToJoin.map(async roomId => Promise.all([this.enterRoomPresence(roomId), this.subscribeToRoomPresence(roomId), this.subscribeToNotifications(roomId)])));
      }
      await this.subscribeToUserInbox();
      logger.info({
        action: 'join',
        message: 'joined successfully',
        meta: {
          connectionId: this.connectionManager.getConnectionIds()[0],
          connectionIds: this.connectionManager.getConnectionIds(),
          newRoomIds,
          roomsToJoin,
          roomsToLeave,
          isFirstJoin
        }
      });
    } catch (error) {
      logError(error, 'join', {
        roomIds,
        isFirstJoin
      });
    } finally {
      this.joinPromise = null;
    }
    return this;
  }
  /**
   * Join the tenant presence channel. Call anytime after join() to subscribe to
   * tenant-scoped presence (e.g. active providers). Sets the internal flag so
   * presence updates include the tenant channel. Safe to call multiple times.
   * No-op if realtime is not connected.
   */
  async joinTenantChannel() {
    if (!this.shouldAttemptJoinTenantChannel) {
      return;
    }
    this.isJoinTenantChannelInProgress = true;
    try {
      await Promise.all([this.enterTenantPresence(), this.subscribeToTenantPresence()]);
      this.hasJoinedTenantChannel = true;
    } catch (error) {
      logError(error, 'joinTenantChannel');
      throw error;
    } finally {
      this.isJoinTenantChannelInProgress = false;
    }
  }
  async initConnectionManager() {
    // create the first connection if it doesn't exist
    return this.connectionManager.initialize({
      closeOnUnload: false,
      // sets ably log level, default is 1 (error)
      logLevel: this.getVendorLogLevel?.() ?? 1,
      logHandler: (message, level) => {
        logger.debug({
          action: 'Ably SDK',
          message: redactLogMessage(message),
          meta: {
            ablyLogLevel: level
          }
        });
      },
      restHost: ABLY_REST_HOST,
      realtimeHost: ABLY_REALTIME_HOST,
      fallbackHosts: ABLY_FALLBACK_HOSTS,
      authCallback: async (tokenParams, callback) => {
        try {
          const tokenRequest = await this.generateAblyTokenRequest();
          if (!tokenRequest || !tokenRequest.timestamp) {
            this.clientLeave();
            const AblyModule = await import('ably');
            const error = new AblyModule.ErrorInfo('Invalid or missing token request response', 40142, 401);
            logError(error, 'authCallback');
            callback(error, null);
            return;
          }
          callback(null, tokenRequest);
        } catch (error) {
          logError(error, 'authCallback');
          this.clientLeave();
          callback(error, null);
        }
      },
      echoMessages: false
    });
  }
  async leave(isLeavingPage) {
    try {
      if (this.connectionManager.isInitialized()) {
        this.clientLeave();
        await this.serverLeave({
          files: {
            url: this._picture ?? ''
          }
        });
        logger.info({
          action: 'leave',
          message: 'left successfully',
          meta: {
            isLeavingPage
          }
        });
      }
    } catch (error) {
      logError(error, 'leave');
      throw error;
    }
  }
  async refresh(roomIds = this.roomIds, isReset = true) {
    if (!this.connectionManager.isInitialized()) {
      throw new Error('Ably client is not initialized');
    }
    const joinedIds = Object.keys(this._membersMap);
    try {
      await Promise.all(roomIds.map(async roomId => {
        const channelName = ChannelMaps.presenceSubscribable(this.identity, roomId, this.tenantId);
        const channel = await this.connectionManager.getChannel(channelName);
        if (!channel) {
          logger.error({
            action: 'refresh',
            message: `Could not find channel for room ${roomId}`,
            meta: {
              roomId,
              channelName
            }
          });
          return;
        }
        const presence = await channel.presence.get();
        const newMembersMap = Object.fromEntries(presence.map(({
          clientId,
          connectionId,
          data
        }) => {
          const identity = getIdentity(clientId);
          return [clientId, this.getNewMember(identity, connectionId, data, roomId)];
        }));
        const room = this._roomsMap[roomId] = this._roomsMap[roomId] || new Room(roomId);
        room.update(newMembersMap, isReset);
      }));
    } catch (error) {
      logError(error, 'refresh', {
        roomIds
      });
      throw error;
    }
    const membersDiff = difference(joinedIds, Object.keys(this._membersMap));
    if (membersDiff.length > 0) {
      logger.warn({
        action: 'refresh',
        message: 'Some of the existing members were not returned with presence.get, isReset indicates if we keep them',
        meta: {
          isReset,
          idsDiff: membersDiff,
          membersMapDiff: Object.fromEntries(membersDiff.map(id => [id, Realtime.memberToLog(this._membersMap[id])]))
        }
      });
    }
    logger.debug({
      action: 'refresh',
      message: 'Refreshed successfully',
      meta: {
        roomIds,
        joinedMemberIds: Object.keys(this._membersMap),
        membersByRoom: mapValues(this._roomsMap, room => mapValues(room.membersMap, Realtime.memberToLog))
      }
    });
  }
  async setState(state) {
    return this.enqueueStateMutation(async () => {
      this._state = {
        __internal: this.getInternalState(),
        payload: state
      };
      await this.updatePresence();
      return this._state?.payload ?? {};
    });
  }
  async updateState(partial) {
    return this.enqueueStateMutation(async () => {
      const current = this._state?.payload ?? {};
      const merged = {
        ...current,
        ...partial
      };
      this._state = {
        __internal: this.getInternalState(),
        payload: merged
      };
      await this.updatePresence();
      return this._state?.payload ?? {};
    });
  }
  async getState() {
    return this._state?.payload ?? {};
  }
  /**
   * Enqueue a `_state` mutation behind any in-flight `join()` and any earlier
   * mutation. The returned promise resolves with the task's value (or rejects
   * with its error); the internal chain swallows errors so a single failing
   * mutation can't permanently break subsequent ones.
   */
  enqueueStateMutation(task) {
    const next = this.stateMutationChain.then(async () => {
      // `_join` writes `_state.payload` after `serverJoin` completes; awaiting
      // here guarantees the read-merge-write inside `task` observes that write.
      await this.awaitInFlightJoin();
      return task();
    });
    this.stateMutationChain = next.catch(() => {});
    return next;
  }
  async getPicture() {
    return this._picture;
  }
  async sendMessage(identity, message) {
    if (!this.identity.id || !identity.id) {
      logger.error({
        action: 'sendMessage',
        message: 'Identity is not defined',
        meta: {
          to: identity,
          roomId: this._membersMap[identity.id]?.roomId
        }
      });
      return;
    }
    const member = this._membersMap[identity.id];
    if (!member) {
      logger.error({
        action: 'sendMessage',
        message: 'Member is not found',
        meta: {
          to: identity
        }
      });
      return;
    }
    if (!member.roomId) {
      logger.error({
        action: 'sendMessage',
        message: 'Member roomId is not found',
        meta: {
          to: identity
        }
      });
      return;
    }
    const channelName = ChannelMaps.notifications(identity, member.roomId, this.tenantId);
    try {
      const channel = await this.connectionManager.getChannel(channelName);
      if (!channel) return;
      if (this.isValidChannelState(channel)) {
        await channel.publish({
          name: 'notificationMessage',
          data: {
            message: this.encrypt(message, identity)
          }
        });
        logger.debug({
          action: 'sendMessage',
          message: 'Message sent',
          meta: {
            to: identity,
            roomId: member.roomId,
            channel: channel.name
          }
        });
      }
    } catch (error) {
      logError(error, 'sendMessage', {
        to: identity,
        roomId: member.roomId
      });
    }
  }
  async acknowledgeMessage(message) {
    try {
      const member = this._membersMap[message.from.id];
      if (!member || !member.roomId) {
        throw new Error(`Could not find member with identity: ${JSON.stringify(message.from)}`);
      }
      const channelName = ChannelMaps.notifications(member.identity, member.roomId, this.tenantId);
      const channel = await this.connectionManager.getChannel(channelName);
      if (!channel || !this.isValidChannelState(channel)) {
        const error = new Error(`Channel ${channelName} is not present or in invalid state`);
        error.statusCode = 90001;
        throw error;
      }
      // Message receiver will send read receipt back to the sender
      // so we don't need to acknowledge the acknowledgement.
      if (message.data) {
        message.data.requestAck = false;
      }
      await channel?.publish({
        name: 'readReceipt',
        data: {
          message: this.encrypt(message.data, member.identity)
        },
        extras: {
          ref: {
            type: 'delivered',
            timeserial: message?.extras?.timeserial
          }
        }
      });
      logger.debug({
        action: 'acknowledgeMessage',
        message: 'Sent read receipt',
        meta: {
          to: member.identity,
          roomId: member.roomId,
          channel: channel.name
        }
      });
      return {
        error: false,
        statusCode: 200
      };
    } catch (error) {
      const {
        data,
        ...messageWithoutData
      } = message;
      logError(error, 'acknowledgeMessage', {
        to: message.from,
        message: messageWithoutData,
        roomId: this._membersMap[message.from.id]?.roomId
      });
      return {
        error: true,
        statusCode: error?.statusCode ?? 500,
        errorData: error
      };
    }
  }
  async setPicture(file) {
    if (!file) {
      return;
    }
    if (isFileData(file)) {
      const downloadLink = await this.uploadProfilePicture({
        contentType: 'image/png',
        filename: `${file.name}.png`,
        privacy: 'private-ephemeral',
        ttl: 2 * 60 * 60,
        region: 'us',
        tags: {}
      }, file);
      this._picture = downloadLink;
    } else {
      this._picture = file.url;
    }
    await this.updatePresence();
    logger.debug({
      action: 'setPicture',
      message: 'Picture set',
      meta: {
        hasPicture: !!this._picture
      }
    });
  }
  async serverMessageToAllUsers(users, payload) {
    if (this.identity.role !== RealtimeRole$1.System) {
      const error = new RealtimeError(`Your role ${this.identity.role} is not authorized to send message to users`);
      logError(error, 'serverMessageToAllUsers', {
        users
      });
      throw error;
    }
    await Promise.all(users.map(async user => this.sendMessageToUser(user, payload)));
    logger.debug({
      action: 'serverMessageToAllUsers',
      message: 'message sent',
      meta: {
        users
      }
    });
  }
  get canJoinTenantChannel() {
    return this.isProvider && Boolean(this.tenantId); // only providers can join tenant channel
  }
  get shouldAttemptJoinTenantChannel() {
    return this.canJoinTenantChannel && this.connectionManager.isInitialized() && this.connectionManager.isConnected() && !this.isJoinTenantChannelInProgress && !this.hasJoinedTenantChannel;
  }
  get isProvider() {
    return this.identity.role === RealtimeRole$1.Provider;
  }
  async enterPresenceChannel(channelName, encryptedState) {
    const channel = await this.connectionManager.getChannel(channelName);
    if (!channel || this.subscribedChannels.has(channel.name)) return;
    this.listenChannelStateChanges(channel);
    try {
      await channel.presence.enter(encryptedState);
      this.subscribedChannels.add(channel.name);
    } catch (error) {
      logError(error, 'enterPresenceChannel', {
        channel: channel.name
      });
    }
    logger.debug({
      action: 'enterPresenceChannel',
      message: 'Presence channel entered',
      meta: {
        channel: channelName,
        tenantId: this.tenantId
      }
    });
  }
  async enterRoomPresence(roomId) {
    const channelName = ChannelMaps.presence(this.identity, roomId, this.tenantId);
    return this.enterPresenceChannel(channelName, this.getPresenceUpdateObject('room'));
  }
  async enterTenantPresence() {
    if (!this.tenantId || !this.isProvider) {
      // only providers can enter tenant presence
      return;
    }
    const channelName = ChannelMaps.tenantProvidersPresence(this.tenantId);
    await this.enterPresenceChannel(channelName, this.getPresenceUpdateObject('tenant'));
  }
  async subscribeToPresenceChannel(channelName, callback) {
    const channel = await this.connectionManager.getChannel(channelName);
    if (!channel || this.subscribedChannels.has(channel.name)) return;
    this.listenChannelStateChanges(channel);
    try {
      await channel.presence.subscribe(callback);
      this.subscribedChannels.add(channel.name);
    } catch (error) {
      logError(error, 'subscribeToPresence', {
        channel: channel.name
      });
    }
  }
  async subscribeToTenantPresence() {
    if (!this.tenantId || !this.isProvider) {
      // only providers can subscribe to tenant presence
      return;
    }
    const channelName = ChannelMaps.tenantProvidersPresence(this.tenantId);
    await this.subscribeToPresenceChannel(channelName, async presence => {
      this._tenant = this._tenant ?? new Tenant({});
      await this.handlePresence(presence, this._tenant);
    });
  }
  async subscribeToRoomPresence(roomId) {
    const channelName = ChannelMaps.presenceSubscribable(this.identity, roomId, this.tenantId);
    await this.subscribeToPresenceChannel(channelName, async presence => {
      const room = this._roomsMap[roomId] = this._roomsMap[roomId] || new Room(roomId);
      await this.handlePresence(presence, room);
    });
  }
  /**
   * Waits for any in-flight `join()` to finish. Used before writing `this._state`
   * in `setState`, and at the start of `updatePresence` so Ably never publishes
   * against stale `roomIds`, capabilities, or presence enters.
   */
  async awaitInFlightJoin() {
    if (this.joinPromise) {
      await this.joinPromise;
    }
  }
  async updatePresence() {
    try {
      // Every presence publish must run after a completed `join()` so `roomIds`,
      // token capabilities, and `subscribedChannels` match. This is not only
      // `setState`'s responsibility: other paths (e.g. `setPicture`) call here too.
      await this.awaitInFlightJoin();
      // When in disconnected state, we can continue to publish messages, however,
      // attempting to update presence while disconnected will throw an error.
      if (this.connectionManager.isConnected()) {
        const roomPresenceUpdate = this.getPresenceUpdateObject('room');
        await Promise.all(this.roomIds.map(async roomId => {
          const channelName = ChannelMaps.presence(this.identity, roomId, this.tenantId);
          const channel = await this.connectionManager.getChannel(channelName);
          if (!channel || !this.isValidChannelState(channel)) return;
          if (!this.subscribedChannels.has(channel.name)) return;
          await channel.presence.update(roomPresenceUpdate);
        }));
        if (this.hasJoinedTenantChannel) {
          const tenantPresenceUpdate = this.getPresenceUpdateObject('tenant');
          const tenantChannel = await this.connectionManager.getChannel(ChannelMaps.tenantProvidersPresence(this.tenantId));
          if (tenantChannel && this.isValidChannelState(tenantChannel)) {
            await tenantChannel.presence.update(tenantPresenceUpdate);
          }
        }
        logger.debug({
          action: 'updatePresence',
          message: 'Presence updated',
          meta: {
            roomIds: this.roomIds
          }
        });
      }
    } catch (error) {
      logError(error, 'updatePresence', {
        roomIds: this.roomIds
      });
    }
  }
  getPresenceUpdateObject(scope) {
    const statePayload = {
      state: this._state.payload ?? {},
      picture: this._picture
    };
    const payload = scope === 'tenant' ? this.encryptTenantState(statePayload) : this.encryptState(statePayload);
    return {
      payload,
      __internal: this._state.__internal
    };
  }
  async handlePresence(presence, membersCollection) {
    const identity = getIdentity(presence.clientId);
    if (identity.userId !== undefined && this.identity.userId === identity.userId) {
      return;
    }
    const member = this.getNewMember(identity, presence.connectionId, presence.data, membersCollection instanceof Room ? membersCollection.id : undefined);
    /**
     * We expect all the members to come with state.
     * If not, we need to handle it accordingly
     */
    if (!member.hasState && presence.action !== 'leave') {
      logger.error({
        action: 'handlePresence',
        message: 'Expected member to have state, but it does not',
        meta: {
          roomIds: this.roomIds,
          userId: identity.userId
        }
      });
      return;
    }
    const event = {
      type: presence.action === 'leave' ? RealtimeEventType.Leave : RealtimeEventType.Join,
      roomId: member.roomId,
      from: member.identity,
      data: membersCollection.membersMap[member.id]?.hasState ? {
        state: membersCollection.membersMap[member.id]?.state ?? {},
        picture: membersCollection.membersMap[member.id]?.picture
      } : undefined,
      created: new Date(Number(presence.timestamp))
    };
    switch (presence.action) {
      case 'enter':
      case 'present':
        {
          if (!membersCollection.membersMap[member.id]) {
            membersCollection.addMember(member);
          } else {
            membersCollection.updateMember(member);
          }
          this.fire([event]);
          break;
        }
      case 'leave':
        {
          if (membersCollection.membersMap[member.id] && membersCollection.membersMap[member.id]?.connectionId === member.connectionId) {
            membersCollection.removeMember(member);
            this.fire([event]);
            break;
          }
          break;
        }
      case 'update':
        {
          membersCollection.updateMember(member);
          this.fire([event]);
          break;
        }
      default:
        {
          logger.warn({
            action: 'handlePresence',
            message: `Unknown presence action: ${presence.action}`,
            meta: {
              roomIds: this.roomIds
            }
          });
          break;
        }
    }
    const {
      data: _data,
      ...pmWithoutData
    } = presence;
    const {
      data: _data2,
      ...eventWithoutData
    } = event;
    logger.debug({
      action: 'handlePresence',
      message: `Presence event handled: ${presence.action}`,
      meta: {
        pm: pmWithoutData,
        event: eventWithoutData,
        from: member.identity,
        roomId: member.roomId
      }
    });
  }
  getNewMember(identity, connectionId, encryptedState, roomId) {
    const stateWrapper = this.decryptState(identity, encryptedState);
    return new Member(identity, roomId, {
      __internal: encryptedState.__internal,
      payload: stateWrapper?.state
    }, stateWrapper?.picture, connectionId);
  }
  /**
   * Listener for the Ably connection state changes.
   * Logs the connection state and takes appropriate actions for certain states.
   */
  onConnectionStateChange(state, connectionId) {
    const logItem = {
      action: 'onConnectionStateChange',
      message: `Ably connection state changed to ${state.current}`,
      meta: {
        roomIds: this.roomIds,
        state,
        connectionId
      }
    };
    switch (state.current) {
      case 'suspended':
      case 'disconnected':
        {
          logger.warn(logItem);
          break;
        }
      case 'failed':
        {
          logger.error(logItem);
          this.leave();
          break;
        }
      default:
        {
          logger.info(logItem);
          break;
        }
    }
  }
  /**
   * Listens for state changes on the specified channel.
   * Should be called once when subscribing to the channel to avoid multiple listeners.
   */
  listenChannelStateChanges(channel) {
    let hasBeenSuspended = false;
    channel?.on(state => {
      const getLogItem = (meta = {}) => ({
        action: 'listenChannelStateChanges',
        message: `Channel state changed to ${state.current}`,
        meta: {
          roomId: ChannelMaps.getRoomIdFromChannel(channel.name),
          channel: channel.name,
          state,
          ...meta
        }
      });
      switch (state.current) {
        case 'failed':
          {
            logger.error(getLogItem());
            break;
          }
        case 'suspended':
          {
            logger.warn(getLogItem());
            break;
          }
        case 'attached':
          {
            const hasLostContinuity = hasBeenSuspended;
            logger['debug'](getLogItem({
              hasLostContinuity
            }));
            break;
          }
        default:
          {
            logger.debug(getLogItem());
            break;
          }
      }
    });
  }
  handleMessageEvent(me, type = RealtimeEventType.Message) {
    const identity = getIdentity(me.clientId);
    const member = this._membersMap[identity.id];
    const data = me.data.message.encryptionData ? this.decrypt(me.data.message, identity) : me.data.message;
    const event = {
      type,
      from: identity,
      messageId: String(me.timestamp),
      roomId: member?.roomId,
      data,
      created: new Date(Number(me.timestamp)),
      extras: me.extras
    };
    this.fire([event]);
    logger.debug({
      action: 'handleMessageEvent',
      message: 'Message received',
      meta: {
        from: identity,
        roomId: member?.roomId
      }
    });
  }
  async subscribeToNotifications(roomId) {
    const channel = await this.connectionManager.getChannel(ChannelMaps.notifications(this.identity, roomId, this.tenantId));
    if (!channel || this.subscribedChannels.has(channel.name)) return;
    this.listenChannelStateChanges(channel);
    try {
      await channel.subscribe('notificationMessage', async message => {
        this.handleMessageEvent(message);
      });
      await channel.subscribe('readReceipt', message => {
        this.handleMessageEvent(message, RealtimeEventType.MessageAck);
      });
      this.subscribedChannels.add(channel.name);
    } catch (error) {
      logError(error, 'subscribeToNotifications', {
        roomId,
        channel: channel.name
      });
    }
  }
  async subscribeToUserInbox() {
    if (!this.isProvider || !this.identity.userId) return;
    const channel = await this.connectionManager.getChannel(ChannelMaps.userInbox(this.identity.userId));
    if (!channel || this.subscribedChannels.has(channel.name)) return;
    this.listenChannelStateChanges(channel);
    try {
      await channel.subscribe('userInbox', message => {
        this.handleMessageEvent(message);
      });
      this.subscribedChannels.add(channel.name);
    } catch (error) {
      logError(error, 'subscribeToUserInbox', {
        channel: channel.name
      });
    }
  }
  async sendMessageToUser(user, payload) {
    const channel = await this.connectionManager.getChannel(ChannelMaps.userInbox(user));
    if (!channel) return;
    try {
      if (this.isValidChannelState(channel)) {
        await channel.publish('userInbox', {
          message: payload
        });
        logger.debug({
          action: 'sendMessageToUser',
          message: 'message sent',
          meta: {
            to: {
              userId: user
            }
          }
        });
      }
    } catch (error) {
      logError(error, 'sendMessageToUser', {
        to: {
          userId: user
        }
      });
    }
  }
  // Encryption
  /**
   * Encrypt message payload
   */
  encrypt(payload, identity) {
    try {
      return encrypt(payload, this.allKeys, this.getMembersKeys(identity));
    } catch (error) {
      throw logError(error, 'encrypt', {
        receiver: identity
      });
    }
  }
  getInternalState() {
    return {
      keys: this.publicKeys
    };
  }
  /**
   * Decrypt message payload
   */
  decrypt(message, identity) {
    try {
      return decrypt(message, this.allKeys, this.getMembersKeys(identity));
    } catch (error) {
      throw logError(error, 'decrypt', {
        sender: identity
      });
    }
  }
  /**
   * Encrypts state for tenant presence
   */
  encryptTenantState(state) {
    try {
      return encryptTenantState(state ?? {}, this.allKeys);
    } catch (error) {
      throw logError(error, 'encryptTenantState', {
        state
      });
    }
  }
  /**
   * Encrypt state payload (user input), expect and return whole state object
   */
  encryptState(state, keys = this.allKeys) {
    try {
      return encryptState(state ?? {}, keys);
    } catch (error) {
      throw logError(error, 'encryptState', {
        state
      });
    }
  }
  /**
   * Decrypt state payload (user input), expect and return whole state object
   */
  decryptState(identity, state) {
    if (!state) {
      return undefined;
    }
    try {
      if (isRealtimeEncryptedStateWrapper(state)) {
        return decryptState(state.payload, this.allKeys, this.getKeysFromState(state)) ?? {};
      }
      return state;
    } catch (error) {
      throw logError(error, 'decryptState', {
        sender: identity
      });
    }
  }
  async generateAblyTokenRequest() {
    if (!this.token) {
      const roomIds = this.roomIds;
      const {
        id,
        role,
        userId
      } = this.identity;
      logger.error({
        action: 'generateAblyTokenRequest',
        message: 'Skipped: realtime access token missing; Ably auth cannot call generateTokenRequest without Authorization',
        meta: {
          roomIds,
          roomId: roomIds.length === 1 ? roomIds[0] : undefined,
          role,
          realtimeIdentityId: id || undefined,
          userId,
          tenantId: this.tenantId,
          connectionManagerInitialized: this.connectionManager.isInitialized()
        }
      });
      return undefined;
    }
    const response = await this.checkTokenAndGraphqlExecute(GenerateTokenRequestMutation);
    return response.Realtime.generateTokenRequest;
  }
  /**
   * Determines whether the provided `channel` is in a valid state and is ready for operations.
   */
  isValidChannelState(channel) {
    if (!channel) return false;
    const state = channel.state;
    switch (state) {
      case 'attached':
      case 'attaching': // Message will be queued and sent once channel is attached
      case 'initialized':
        // Channel will implicitly attach when publishing/subscribing
        return true;
      case 'detaching':
      case 'detached':
      case 'suspended':
      case 'failed':
        return false;
      default:
        return false;
    }
  }
  clientLeave() {
    logger.info({
      action: 'clientLeave',
      message: 'Closing all connections to Ably and clearing all channels'
    });
    this.subscribedChannels.clear();
    this.hasJoinedTenantChannel = false;
    this.isJoinTenantChannelInProgress = false;
    // Close all connections through the connection manager
    this.connectionManager.close();
  }
  /**
   * Leave a specific room: leave presence, unsubscribe, and release all channels.
   * Does NOT call serverLeave, only local cleanup.
   */
  async leaveRoom(roomId) {
    try {
      // Leave presence
      await this.leavePresence(roomId);
      // Unsubscribe from presence subscribable channel
      await this.unsubscribeFromPresence(roomId);
      // Unsubscribe from notifications channel
      await this.unsubscribeFromNotifications(roomId);
      // Fire leave events for all members in this room and remove the room object
      this.cleanUpRoom(roomId);
    } catch (error) {
      logError(error, 'leaveRoom', {
        roomId
      });
    }
  }
  async leavePresence(roomId) {
    const presenceChannelName = ChannelMaps.presence(this.identity, roomId, this.tenantId);
    const presenceChannel = await this.connectionManager.getChannel(presenceChannelName);
    if (!presenceChannel || !this.subscribedChannels.has(presenceChannel.name)) {
      return;
    }
    try {
      await presenceChannel.presence.leave();
      this.subscribedChannels.delete(presenceChannel.name);
      await this.connectionManager.releaseChannel(presenceChannelName);
    } catch (error) {
      throw logError(error, 'leavePresence', {
        roomId
      });
    }
  }
  async unsubscribeFromPresence(roomId) {
    const subscribableChannelName = ChannelMaps.presenceSubscribable(this.identity, roomId, this.tenantId);
    const subscribableChannel = await this.connectionManager.getChannel(subscribableChannelName);
    if (!subscribableChannel || !this.subscribedChannels.has(subscribableChannel.name)) {
      return;
    }
    try {
      subscribableChannel.presence.unsubscribe();
      this.subscribedChannels.delete(subscribableChannel.name);
      await this.connectionManager.releaseChannel(subscribableChannelName);
    } catch (error) {
      throw logError(error, 'unsubscribeFromPresence', {
        roomId
      });
    }
  }
  async unsubscribeFromNotifications(roomId) {
    const notificationsChannelName = ChannelMaps.notifications(this.identity, roomId, this.tenantId);
    const notificationsChannel = await this.connectionManager.getChannel(notificationsChannelName);
    if (!notificationsChannel || !this.subscribedChannels.has(notificationsChannel.name)) {
      return;
    }
    try {
      notificationsChannel.unsubscribe();
      this.subscribedChannels.delete(notificationsChannel.name);
      await this.connectionManager.releaseChannel(notificationsChannelName);
    } catch (error) {
      throw logError(error, 'unsubscribeFromNotifications', {
        roomId
      });
    }
  }
  /**
   * Clean up room from local state and fire leave events for all members in this room
   */
  cleanUpRoom(roomId) {
    const room = this._roomsMap[roomId];
    if (room) {
      const leaveEvents = Object.values(room.membersMap).map(member => ({
        type: RealtimeEventType.Leave,
        roomId,
        from: member.identity,
        data: member.hasState ? {
          state: member.state,
          picture: member.picture
        } : undefined,
        created: new Date()
      }));
      if (leaveEvents.length > 0) {
        this.fire(leaveEvents);
      }
    }
    // Clean up room from local state
    delete this._roomsMap[roomId];
  }
  static memberToLog(member) {
    if (!member) {
      return null;
    }
    return {
      identity: member.identity,
      hasState: Object.keys(member.state).length > 0,
      hasPicture: Boolean(member.picture)
    };
  }
}
function clientSideCode(callback) {
  if (typeof window !== 'undefined') {
    callback(window);
  }
}

export { ABLY_FALLBACK_HOSTS, ABLY_REALTIME_HOST, ABLY_REST_HOST, AuthError, BackendError, BaseLogger, CH_SEPARATOR, ChannelMaps, ClientError, DEFAULT_RETRY_OPTIONS, Endpoint, LogLevel, ClientLogger as Logger, Member, NAMESPACE_WILDCARD, Realtime, RealtimeError, RealtimeEventType, RealtimeRole$1 as RealtimeRole, RealtimeStatus, Room, VERSION, baseLogger, index as encryption, index$3 as fetch, getErrorMessage, getErrorStack, index$2 as ids, logError$2 as logError, clientLogger as logger, index$1 as realtimeId, redactLogMessage, makeOperationWithRetry as retry };
